import { ApplyFunction, ApplyFunctionUtils } from "../ApplyFunction";
import { ISafeObject, SafeObjectUtils } from "../ISafeObject";

// ICmd interface extending ISafeObject
export interface ICmd extends ISafeObject {
  readonly SIGNATURE: string;
  readonly originalObjectSIG: string;
  readonly comment: string;
  readonly applyFunction?: ApplyFunction;
  readonly parentGroup: string;
  readonly execute: boolean;
}

// Utility functions for ICmd
export const ICmdUtils = {
  // Create a new ICmd
  create({
    cls,
    id,
    parentGroup,
    execute,
    originalObjectSIG = "",
    SIGNATURE = "",
    comment = "",
    applyFunction,
  }: {
    cls: string;
    id: string;
    parentGroup: string;
    execute: boolean;
    originalObjectSIG?: string;
    SIGNATURE?: string;
    comment?: string;
    applyFunction?: ApplyFunction;
  }): ICmd {
    return {
      ...SafeObjectUtils.create({
        cls,
        id,
      }),
      SIGNATURE,
      originalObjectSIG,
      comment,
      applyFunction,
      parentGroup,
      execute,
    };
  },

  // Create from map
  fromMap(map: { [key: string]: any }): ICmd {
    try {
      return {
        ...SafeObjectUtils.fromMap(map),
        parentGroup: map["parentGroup"] ?? "",
        execute: map["execute"] ?? true,
        originalObjectSIG: map["originalObjectSIG"] ?? "",
        SIGNATURE: map["SIGNATURE"] ?? "",
        comment: map["comment"] ?? "",
        applyFunction: map["applyFunction"]
          ? ApplyFunctionUtils.fromMap(map["applyFunction"])
          : undefined,
      };
    } catch (e) {
      console.error(e);
      throw new Error(e as string);
    }
  },

  // Create empty ICmd
  empty(): ICmd {
    return {
      ...SafeObjectUtils.empty(),
      parentGroup: "",
      execute: true,
      originalObjectSIG: "",
      SIGNATURE: "",
      comment: "",
    };
  },

  // Edit SIGNATURE (returns new object since we're keeping immutability)
  editSIGNATURE(cmd: ICmd, SIGNATURE: string): ICmd {
    return { ...cmd, SIGNATURE };
  },

  // Edit comment (returns new object)
  editComment(cmd: ICmd, comment: string): ICmd {
    return { ...cmd, comment };
  },

  // Copy with modifications
  copyWith(
    cmd: ICmd,
    {
      originalObjectSIG,
      SIGNATURE,
      comment,
      applyFunction,
      execute,
      parentGroup,
    }: {
      originalObjectSIG: string;
      SIGNATURE?: string;
      comment?: string;
      applyFunction?: ApplyFunction;
      execute?: boolean;
      parentGroup?: string;
    }
  ): ICmd {
    return {
      cls: cmd.cls,
      id: cmd.id,
      parentGroup: parentGroup ?? cmd.parentGroup,
      execute,
      originalObjectSIG,
      SIGNATURE,
      comment,
      applyFunction,
    };
  },

  // Convert to map
  toMap(cmd: ICmd): { [key: string]: any } {
    const applyFunctionCls = cmd.cls.split("-")[0];
    const json: { [key: string]: any } = SafeObjectUtils.toMap({ ...cmd, cls: applyFunctionCls });
    json["parentGroup"] = cmd.parentGroup;
    json["SIGNATURE"] = cmd.SIGNATURE;
    json["comment"] = cmd.comment;
    json["execute"] = cmd.execute;

    if (cmd.originalObjectSIG !== "") {
      json["originalObjectSIG"] = cmd.originalObjectSIG;
    }

    if (cmd.applyFunction && cmd.applyFunction.functionName !== "") {
      json["applyFunction"] = ApplyFunctionUtils.toMap(cmd.applyFunction);
    }

    return json;
  },

  // Convert to JSON
  toJson(cmd: ICmd): string {
    return JSON.stringify(ICmdUtils.toMap(cmd));
  },
};
